import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectBoat, setCurrentUser } from '../../actions';
import { addToFavorites, removeFromFavorites } from '../../firebase/firebase';
import { getBoatImageUrl } from '../../utils/boatUtils';
import { formatCurrency } from '../../utils/currency';
import { getBoatPriceUnit } from '../../utils/priceUtils';
import { Rating, Icon, Button, Modal, Image, Header, Segment, Dimmer, Loader } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import './BoatCard.css';

/**
 * A reusable card component to display boat information.
 * @param {object} boat - The boat data object from Firestore.
 */
const ReusableBoatCard = ({ boat, actions, isOwnerView = false, onFavoriteToggle = () => {}, onToggleCompare, isComparing = false, compareDisabled = false }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const [isFavoriting, setIsFavoriting] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const functions = getFunctions();

  const isFavorite = currentUser?.favorites?.includes(boat.uid);

  const imageUrl = getBoatImageUrl(boat, 0);
  const averageRating = boat.averageRating || 0;
  const totalReviews = boat.totalReviews || 0;

  const { price, priceUnit } = useMemo(() => {
    let price = 0;
    const hasSlots = boat.availabilitySlots && boat.availabilitySlots.length > 0;

    if (hasSlots) {
      const prices = boat.availabilitySlots.map(slot => slot.price).filter(p => p != null && p > 0);
      if (prices.length > 0) {
        price = Math.min(...prices);
      }
    } else {
      // Fallback to legacy pricing
      price = boat.bookingTimeframe === 'Hourly' ? boat.hourlyBookingPrice : boat.dailyBookingPrice;
    }

    return { price, priceUnit: getBoatPriceUnit(boat) };
  }, [boat]);

  const features = useMemo(() => {
    const items = [];

    // Captain Status
    if (boat.captain === 'With Captain') {
      items.push({ icon: 'user', text: 'Captain Included' });
    }

    // Duration Constraints
    if (boat.isMultiDay) {
      // For multi-day, check availability slots for minGuests which maps to days in the data model
      if (boat.availabilitySlots && boat.availabilitySlots.length > 0) {
        const minDaysValues = boat.availabilitySlots
          .map(s => Number(s.minGuests))
          .filter(n => !isNaN(n) && n > 0);
        
        if (minDaysValues.length > 0) {
          const minDays = Math.min(...minDaysValues);
          if (minDays > 1) {
            items.push({ icon: 'calendar alternate', text: `Min ${minDays} days` });
          }
        }
      }
    } else {
      // For hourly, check minBookingHours
      const minHours = Number(boat.minBookingHours);
      if (!isNaN(minHours) && minHours > 1) {
        items.push({ icon: 'clock', text: `Min ${minHours} hours` });
      }
    }

    // Guest Constraints (Excursion Hourly only)
    if (boat.isExcursion && !boat.isMultiDay) {
      if (boat.availabilitySlots && boat.availabilitySlots.length > 0) {
        const minGuestsValues = boat.availabilitySlots
          .map(s => Number(s.minGuests))
          .filter(n => !isNaN(n) && n > 0);
        
        if (minGuestsValues.length > 0) {
          const minGuests = Math.min(...minGuestsValues);
          if (minGuests > 1) {
            items.push({ icon: 'users', text: `Min ${minGuests} guests` });
          }
        }
      }
    }

    return items;
  }, [boat]);

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser) {
      // This part is tricky with a Link wrapper, but we can handle it.
      // For now, we assume the user is prompted to log in by other means
      // or we can implement a modal. For simplicity, we'll rely on the PrivateRoute.
      return;
    }
    setIsFavoriting(true);
    try {
      if (isFavorite) {
        await removeFromFavorites({ boatId: boat.uid });
        const newFavorites = currentUser.favorites.filter(id => id !== boat.uid);
        dispatch(setCurrentUser({ ...currentUser, favorites: newFavorites }));
        toast.info('Removed from favorites');
        if (onFavoriteToggle) onFavoriteToggle(boat.uid);
      } else {
        await addToFavorites({ boatId: boat.uid, ownerUID: boat.ownerUID });
        const newFavorites = [...(currentUser.favorites || []), boat.uid];
        dispatch(setCurrentUser({ ...currentUser, favorites: newFavorites }));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites.');
      console.error("Favoriting error:", error);
    } finally {
      setIsFavoriting(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const shareUrl = `${window.location.origin}/selectboat/${boat.ownerUID}/${boat.uid}`;
    const shareData = {
      title: `${boat.boatManufacturer} ${boat.boatModel}`,
      text: `Check out this boat on BoatRBO!`,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // Silently fail if user cancels share
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Boat link copied to clipboard!');
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      toast.info('Please log in to report a listing.');
      return;
    }
    const reason = window.prompt("Please describe why you are reporting this boat listing:");
    if (reason) {
      try {
        const reportUserFn = httpsCallable(functions, 'reportUser');
        await reportUserFn({
          reportedUserId: boat.ownerUID,
          reason: `Reported Boat: ${boat.boatManufacturer} ${boat.boatModel} (${boat.uid}). Reason: ${reason}`,
          reporterUserId: currentUser?.id,
        });
        toast.success('Boat listing reported successfully.');
      } catch (error) {
        toast.error('Failed to report boat listing.');
      }
    }
  };

  // Define the main content of the card that will be clickable
  const mainCardContent = (
    <>
      {boat.featured && <div className="featured-banner"><Icon name="star" /> Featured</div>}
      <div className="reservation-card-image-container">
        <LazyLoadImage
          alt={`${boat.boatManufacturer} ${boat.boatModel}`}
          src={imageUrl || 'https://react.semantic-ui.com/images/wireframe/image.png'}
          effect="blur"
          className="reservation-card-image"
          height="100%"
          width="100%"
        />
      </div>
      <div className="reservation-card-content">
        <h3 className="reservation-card-title" style={{ whiteSpace: 'pre-wrap' }}>{`${boat.boatManufacturer} ${boat.boatModel}`}</h3>
        <p className="reservation-card-meta">
          <span className='location'>{boat.city}, {boat.state}</span>
        </p>
        <p className="reservation-card-description">
          {`${boat.boatType} • ${boat.lengthOfBoats} ft • ${boat.boatCapacity} guests`}
        </p>
        {features.length > 0 && (
          <div className="reservation-card-features" style={{ marginTop: '0.5em', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {features.map((f, i) => (
              <span key={i} style={{ fontSize: '0.8em', marginLeft: '10px', color: '#555', display: 'flex', alignItems: 'center', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                <Icon name={f.icon} size='small' style={{ marginRight: '4px' }}/> {f.text}
              </span>
            ))}
          </div>
        )}
        {boat.boatDescription && (
          <p className="reservation-card-description" style={{ marginTop: '0.5em' }}>
            {`${boat.boatDescription.substring(0, 75)}${boat.boatDescription.length > 75 ? '...' : ''}`}
          </p>
        )}
      </div>
    </>
  );

  return (
    <div className="reusable-card-container">
      <div className="reservation-card-wrapper">
        {/* DEFINITIVE FIX: Move the favorite icon outside the Link wrapper to prevent navigation on click.
            The parent 'reservation-card-wrapper' is already positioned relatively, so the icon's absolute positioning will work correctly. */}
        {!isOwnerView && currentUser && (
          <Icon name={isFavorite ? 'heart' : 'heart outline'} color={isFavorite ? 'red' : 'grey'} className="favorite-icon" loading={isFavoriting} disabled={isFavoriting} onClick={handleFavoriteClick} />
        )}
        <Link
          to={`/selectboat/${boat.ownerUID}/${boat.uid}`}
          onClick={() => dispatch(selectBoat(boat))}
          className="reservation-card-main-link"
        >
          {mainCardContent}
        </Link>
        <div className="reservation-card-actions">
          {actions || (
            <>
              <div className="review-display"><Rating icon='star' defaultRating={Math.round(averageRating)} maxRating={5} disabled size="small" /><span className="review-count">({totalReviews})</span></div>
              <div className="price-display"><strong>{formatCurrency(price, boat.currency)}</strong> / {priceUnit}</div>
            </>
          )}
        </div>
        <div className="boat-card-extra-actions">
            <Button basic size='mini' icon onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickViewOpen(true); }} title="Quick View">
                <Icon name="eye" />
            </Button>
            {onToggleCompare && (
              <Button 
                basic 
                size='mini'
                color={isComparing ? "green" : null} 
                icon 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleCompare(boat); }} 
                title="Compare" 
                disabled={compareDisabled && !isComparing}
              >
                <Icon name="exchange" />
              </Button>
            )}
            <Button basic size='mini' icon onClick={handleShare} title="Share Boat">
                <Icon name="share alternate" />
            </Button>
            {!isOwnerView && currentUser && (
                <Button basic size='mini' icon color='red' onClick={handleReport} title="Report Boat">
                    <Icon name="flag" />
                </Button>
            )}
        </div>
      </div>

      <Modal open={quickViewOpen} onClose={() => setQuickViewOpen(false)} size="large" closeIcon className="quick-view-modal">
        <Modal.Header>{boat.boatManufacturer} {boat.boatModel}</Modal.Header>
        <Modal.Content scrolling>
            <Carousel showThumbs={true} showStatus={false} useKeyboardArrows>
                {(boat.imageUrls && boat.imageUrls.length > 0 ? boat.imageUrls : [getBoatImageUrl(boat, 0)]).map((url, index) => (
                    <div key={index} style={{ position: 'relative', minHeight: '200px', display: 'flex', justifyContent: 'center', background: '#f0f0f0' }}>
                        {!loadedImages[index] && <Dimmer active inverted><Loader /></Dimmer>}
                        <img 
                            src={url} 
                            alt={`${boat.boatManufacturer} ${boat.boatModel} - ${index + 1}`}
                            onLoad={() => setLoadedImages(prev => ({ ...prev, [index]: true }))}
                            style={{ 
                                visibility: loadedImages[index] ? 'visible' : 'hidden',
                                maxHeight: '60vh',
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }} 
                        />
                    </div>
                ))}
            </Carousel>
            <Segment>
                <Header as="h3">Details</Header>
                <p><strong>Type:</strong> {boat.boatType}</p>
                <p><strong>Length:</strong> {boat.lengthOfBoats} ft</p>
                <p><strong>Capacity:</strong> {boat.boatCapacity} guests</p>
                <Header as="h3">Description</Header>
                <p>{boat.boatDescription || "No description provided."}</p>
            </Segment>
        </Modal.Content>
        <Modal.Actions>
            <Button as={Link} to={`/selectboat/${boat.ownerUID}/${boat.uid}`} primary>
                View Full Details <Icon name='right chevron' />
            </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ReusableBoatCard;
